import React, { useState, useEffect, useRef } from 'react';
import IconUpload from './IconUpload';
import { getOfflineMap, saveOfflineMap } from '../utils/offlineDb';
import OfflineCategoryRenameModal from './OfflineCategoryRenameModal';

function padHexColour(hex) {
  if (!hex) return '#000000';
  hex = hex.trim();
  if (hex[0] !== '#') hex = '#' + hex;
  if (hex.length === 4) {
    return (
      '#' +
      hex[1] + hex[1] +
      hex[2] + hex[2] +
      hex[3] + hex[3]
    );
  }
  if (hex.length === 7) return hex;
  return '#000000';
}

async function savePOIToIndexedDB(poi) {
  const mapId = poi.map_id;
  const bundle = await getOfflineMap(mapId);
  if (!bundle) throw new Error(`Map ${mapId} not found`);

  let pois = Array.isArray(bundle.pois) ? [...bundle.pois] : [];
  const idx = pois.findIndex(p => p.id === poi.id);
  if (idx === -1) {
    pois.push(poi);
  } else {
    pois[idx] = poi;
  }

  bundle.pois = pois;
  bundle.lastEdited = Date.now();

  await saveOfflineMap(mapId, bundle);

  window.dispatchEvent(new CustomEvent('poiUpdated', { detail: { mapId, poi } }));
}

async function saveTypeToIndexedDB(type) {
  const mapId = type.map_id;
  const bundle = await getOfflineMap(mapId);
  if (!bundle) throw new Error(`Map ${mapId} not found`);

  let types = Array.isArray(bundle.poi_types) ? [...bundle.poi_types] : [];
  const idx = types.findIndex(t => t.id === type.id);
  if (idx === -1) {
    types.push(type);
  } else {
    types[idx] = type;
  }

  bundle.poi_types = types;
  bundle.lastEdited = Date.now();

  await saveOfflineMap(mapId, bundle);

  window.dispatchEvent(new CustomEvent('typeUpdated', { detail: { mapId, type } }));
}

function OfflineEditPOI({
  poi,
  customCategories = [],
  customTypes = [],
  categoryObj = {},
  typeObj = {},
  userId,
  mapOwnerId,
  onPOIUpdate,
  onTypeUpdate,
  onCategoryUpdate,
  canEdit,
  onClose,
  allPois = [],
}) {
  const [mode, setMode] = useState('main');
  const [edit, setEdit] = useState({
    poiName: '',
    category: '',
    typeName: '',
    typeColour: '#ff0000',
    typeShape: 'circle',
    labelBorder: 1,
    labelBgColour: '#000000',
    description: '',
    imageUrl: '',
    iconUrl: '',
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [typeChangedFlags, setTypeChangedFlags] = useState({
    name: false,
    color: false,
    shape: false,
    labelBorder: false,
    labelBgColour: false,
    icon: false,
  });

  const inputRef = useRef(null);
  const prevPoiId = useRef();

  // --- Category rename modal state ---
  const [categoryChangeModalOpen, setCategoryChangeModalOpen] = useState(false);
  const [categoryChangeNewName, setCategoryChangeNewName] = useState('');
  const [categoryChangeOldName, setCategoryChangeOldName] = useState('');
  const [connectedTypesToMove, setConnectedTypesToMove] = useState(new Set());
  const [connectedTypesList, setConnectedTypesList] = useState([]);

  useEffect(() => {
    if (!poi || !poi.id) return;
    if (poi.id !== prevPoiId.current && !dirty) {
      const poiType = customTypes.find(t => t.id === poi.type_id);
      const poiCategory = poiType ? customCategories.find(c => c.id === poiType.category_id) : null;
      setEdit({
        poiName: poi.poi_name || '',
        category: poiCategory ? poiCategory.name : '',
        typeName: poiType ? poiType.name : '',
        typeColour: padHexColour(poiType ? poiType.color : '#ff0000'),
        typeShape: poiType ? poiType.shape : 'circle',
        labelBorder: typeof poi.label_border === 'undefined' || poi.label_border === null ? 1 : Number(poi.label_border),
        labelBgColour: padHexColour(poi.label_bg_colour || '#000000'),
        description: poi.description || '',
        imageUrl: poi.image_url || '',
        iconUrl: poi.icon_file || '',
      });
      setTypeChangedFlags({
        name: false,
        color: false,
        shape: false,
        labelBorder: false,
        labelBgColour: false,
        icon: false,
      });
      setSaving(false);
    }
    prevPoiId.current = poi.id;
  }, [poi?.id, customCategories, customTypes, dirty]);

  const categoryId = customCategories.find(c => c.name === edit.category)?.id || null;

  const handleChange = (field, value) => {
    setEdit(prev => ({ ...prev, [field]: value }));
    setDirty(true);

    if (field === 'typeName') {
      setTypeChangedFlags(prev => ({ ...prev, name: true }));
    } else if (field === 'typeColour') {
      setTypeChangedFlags(prev => ({ ...prev, color: true }));
    } else if (field === 'typeShape') {
      setTypeChangedFlags(prev => ({ ...prev, shape: true }));
    } else if (field === 'labelBorder') {
      setTypeChangedFlags(prev => ({ ...prev, labelBorder: true }));
    } else if (field === 'labelBgColour') {
      setTypeChangedFlags(prev => ({ ...prev, labelBgColour: true }));
    } else if (field === 'iconUrl') {
      setTypeChangedFlags(prev => ({ ...prev, icon: true }));
    }
  };

  const toggleConnectedType = (typeId) => {
    setConnectedTypesToMove(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeId)) newSet.delete(typeId);
      else newSet.add(typeId);
      return newSet;
    });
  };

  const savePOIChanges = async (useNewCategoryId = false, newCategoryId = null) => {
    try {
      const { name, ...poiWithoutName } = poi;
      const updatedPOI = {
        id: poi.id,
        map_id: poi.map_id,
        poi_name: edit.poiName,
        description: edit.description,
        icon_file: edit.iconUrl,
        image_url: edit.imageUrl,
        type_id: customTypes.find(t => t.name === edit.typeName)?.id || poi.type_id,
        x: poi.x,
        y: poi.y,
      };

      if (typeof onPOIUpdate === 'function') {
        await onPOIUpdate(updatedPOI);
      }

      await savePOIToIndexedDB(updatedPOI);

      const originalType = customTypes.find(t => t.id === poi.type_id);
      const typeNeedsUpdate = originalType && (
        typeChangedFlags.name ||
        typeChangedFlags.color ||
        typeChangedFlags.shape ||
        typeChangedFlags.labelBorder ||
        typeChangedFlags.labelBgColour ||
        typeChangedFlags.icon
      );

      if (typeNeedsUpdate) {
        const updatedType = {
          ...originalType,
          name: edit.typeName,
          color: padHexColour(edit.typeColour),
          shape: edit.typeShape,
          label_border: Number(edit.labelBorder),
          label_bg_colour: padHexColour(edit.labelBgColour),
          icon: edit.iconUrl,
          map_id: poi.map_id,
        };
        if (typeof onTypeUpdate === 'function') {
          await onTypeUpdate(updatedType);
        }
        await saveTypeToIndexedDB(updatedType);
      }

      if (typeof onPOIUpdate === 'function') {
        await onPOIUpdate(updatedPOI, null, { confirmedDbSave: true });
      }

      window.dispatchEvent(new CustomEvent('offlineMapDataChanged', { detail: { mapId: poi.map_id } }));

      setDirty(false);
      setSaving(false);
      setMode('main');
      setTypeChangedFlags({
        name: false,
        color: false,
        shape: false,
        labelBorder: false,
        labelBgColour: false,
        icon: false,
      });
    } catch (error) {
      setSaving(false);
      console.error('savePOIChanges error:', error);
    }
  };

  const handleSave = async () => {
    if (saving) return;

    const poiType = customTypes.find(t => t.id === poi.type_id);
    const currentCategoryId = poiType ? poiType.category_id : null;
    const currentCategoryName = customCategories.find(c => c.id === currentCategoryId)?.name || '';

    if (edit.category.trim() !== currentCategoryName.trim()) {
      const connectedTypes = currentCategoryId
        ? customTypes.filter(t => t.category_id === currentCategoryId)
        : [];

      if (connectedTypes.length > 0) {
        setConnectedTypesList(connectedTypes);
        setCategoryChangeOldName(currentCategoryName);
        setCategoryChangeNewName(currentCategoryName);
        setConnectedTypesToMove(new Set(connectedTypes.map(t => t.id)));  // all checked initially
        setCategoryChangeModalOpen(true);
        return;
      }
    }

    setSaving(true);
    await savePOIChanges();
  };

  const getPOIsForType = (typeId) => {
    if (!allPois || allPois.length === 0) return [];
    return allPois.filter(poi => poi.type_id === typeId);
  };

  const handleCategoryChangeConfirm = async (typesToMoveSet) => {
    setCategoryChangeModalOpen(false);

    const originalCategory = customCategories.find(c => c.name.toLowerCase().trim() === categoryChangeOldName.toLowerCase().trim());
    if (!originalCategory) {
      await savePOIChanges(false);
      return;
    }

    const newNameTrimmed = categoryChangeNewName.trim();

    if (newNameTrimmed.toLowerCase() === categoryChangeOldName.toLowerCase()) {
      if (typeof onCategoryUpdate === 'function') {
        await onCategoryUpdate(originalCategory.id, newNameTrimmed, false);
      }
      await savePOIChanges(false);
      return;
    }

    if (typesToMoveSet.size > 0) {
      const newCategoryId = Date.now();

      if (typeof onCategoryUpdate === 'function') {
        await onCategoryUpdate(newCategoryId, newNameTrimmed, true);
      }

      if (typeof onTypeUpdate === 'function') {
        for (const type of connectedTypesList) {
          if (typesToMoveSet.has(type.id)) {
            const updatedType = { ...type, category_id: newCategoryId };
            await onTypeUpdate(updatedType);

            if (typeof onPOIUpdate === 'function') {
              const poisToUpdate = getPOIsForType(type.id);
              for (const poiItem of poisToUpdate) {
                const updatedPOI = { ...poiItem };
                await onPOIUpdate(updatedPOI);
                await savePOIToIndexedDB(updatedPOI);
              }
            }
          }
        }
      }

      await savePOIChanges(true, newCategoryId);
    } else {
      if (typeof onCategoryUpdate === 'function') {
        await onCategoryUpdate(originalCategory.id, newNameTrimmed, false);
      }
      await savePOIChanges(false);
    }
  };

  const cancelCategoryChange = () => {
    setCategoryChangeModalOpen(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this POI? This action cannot be undone.')) return;

    try {
      const bundle = await getOfflineMap(poi.map_id);
      if (!bundle) throw new Error('Map not found in IndexedDB');

      const newPois = Array.isArray(bundle.pois) ? bundle.pois.filter(p => p.id !== poi.id) : [];

      const updatedBundle = { ...bundle, pois: newPois, lastEdited: Date.now() };

      await saveOfflineMap(bundle.id, updatedBundle);

      window.dispatchEvent(new CustomEvent('offlineMapDataChanged', { detail: { mapId: bundle.id } }));

      if (typeof onPOIUpdate === 'function') {
        await onPOIUpdate(null, poi.id);
      }

      if (typeof onCategoryUpdate === 'function') {
        onCategoryUpdate(poi.id, edit.category);
      }

      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (error) {
      alert('Failed to delete POI: ' + error.message);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|gif)$/i.test(file.type)) {
      alert("Only PNG, JPG, GIF allowed");
      return;
    }
    if (file.size > 1024 * 1024) {
      alert("Max 1MB image size");
      return;
    }
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { name, ...poiWithoutName } = poi;
      const updatedPOI = { ...poiWithoutName, image_url: base64 };
      if (typeof onPOIUpdate === 'function') {
        await onPOIUpdate(updatedPOI);
      }
      setEdit(prev => ({ ...prev, imageUrl: base64 }));
      setMode('main');
    } catch (error) {
      alert('Failed to upload image.');
    }
  };

  return (
    <>
      {categoryChangeModalOpen && (
        <>
          <OfflineCategoryRenameModal
            mapId={poi.map_id}
            oldName={categoryChangeOldName}
            newName={categoryChangeNewName}
            connectedTypes={connectedTypesList}
            initialTypesToMove={Array.from(connectedTypesToMove)}
            currentTypeId={poi.type_id}
            onConfirm={handleCategoryChangeConfirm}
            onCancel={cancelCategoryChange}
          />
        </>
      )}

      {mode === 'icon-upload' && (
        <IconUpload
          initialIconUrl={edit.iconUrl}
          typeId={poi.type_id}
          mapId={poi.map_id}
          onIconUploaded={async (url) => {
            setEdit(prev => ({ ...prev, iconUrl: url }));
            const { name, ...poiWithoutName } = poi;
            if (typeof onPOIUpdate === 'function') {
              await onPOIUpdate({ ...poiWithoutName, icon_file: url });
            }
            setMode('main');
            if (typeof onCategoryUpdate === 'function') {
              onCategoryUpdate(poi.id, edit.category, true);
            }
          }}
          onCancel={() => setMode('main')}
        />
      )}

      {mode === 'image-upload' && (
        <div style={{
          background: '#232427',
          borderRadius: 12,
          padding: 24,
          marginTop: 10,
          marginBottom: 10,
          minWidth: 340,
          boxShadow: '0 2px 20px #000c',
          color: '#fff',
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
            Upload Image (PNG, JPG, GIF, max 1MB)
          </div>
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif"
            onChange={e => {
              if (e.target.files.length) {
                handleImageUpload(e.target.files[0]);
              }
            }}
          />
          <button
            onClick={() => setMode('main')}
            style={{
              marginTop: 15,
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '7px 18px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {mode === 'main' && (
        <div style={{
          background: '#232427',
          borderRadius: 16,
          boxShadow: '0 6px 24px #000b',
          padding: 22,
          color: '#e2e4e9',
          minWidth: 300,
          fontSize: 15,
          fontFamily: 'inherit',
        }}>
          <div style={{ marginBottom: 12 }}>
            <label><b>Name:</b></label>
            <input
              ref={inputRef}
              value={edit.poiName}
              onChange={e => handleChange('poiName', e.target.value)}
              style={{ marginLeft: 8, width: 'calc(100% - 60px)' }}
              disabled={!canEdit}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label><b>Category:</b></label>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
              <input
                list="categories-list"
                value={edit.category}
                readOnly
                disabled
                style={{
                  flexGrow: 1,
                  maxWidth: 'calc(75% - 90px)',
                  backgroundColor: '#444',
                  marginLeft: '8px'
                }}

              />
              <button
                onClick={() => {
                  const poiType = customTypes.find(t => t.id === poi.type_id);
                  const currentCategoryId = poiType ? poiType.category_id : null;
                  const currentCategoryName = customCategories.find(c => c.id === currentCategoryId)?.name || '';
                  const connectedTypes = currentCategoryId
                    ? customTypes.filter(t => t.category_id === currentCategoryId)
                    : [];
                  setConnectedTypesList(connectedTypes);
                  setCategoryChangeOldName(currentCategoryName);
                  setCategoryChangeNewName(currentCategoryName);
                  setConnectedTypesToMove(new Set(connectedTypes.map(t => t.id)));
                  setCategoryChangeModalOpen(true);
                }}
                disabled={!canEdit}
                style={{
                  marginLeft: 8,
                  backgroundColor: '#4cc153',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 10px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: 13,
                  lineHeight: '1.2',
                }}
              >
                Edit Category
              </button>
              <datalist id="categories-list">
                {customCategories.map(c => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label><b>Type Name:</b></label>
            <input
              list="types-list"
              value={edit.typeName}
              onChange={e => handleChange('typeName', e.target.value)}
              style={{ marginLeft: 8, width: 'calc(100% - 60px)' }}
              disabled={!canEdit}
            />
            <datalist id="types-list">
              {customTypes
                .filter(t => String(t.category_id) === String(categoryId))
                .map(t => (
                  <option key={t.id} value={t.name} />
                ))}
            </datalist>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label><b>Type Colour:</b></label>
            <input
              type="color"
              value={edit.typeColour}
              onChange={e => handleChange('typeColour', padHexColour(e.target.value))}
              disabled={!canEdit}
              style={{ marginLeft: 8, verticalAlign: 'middle' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label><b>Type Shape:</b></label>
            <select
              value={edit.typeShape}
              onChange={e => handleChange('typeShape', e.target.value)}
              disabled={!canEdit}
              style={{ marginLeft: 8 }}
            >
              <option value="circle">Circle</option>
              <option value="square">Square</option>
              <option value="triangle">Triangle</option>
              <option value="star">Star</option>
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label><b>Label Border:</b></label>
            <select
              value={edit.labelBorder}
              onChange={e => handleChange('labelBorder', Number(e.target.value))}
              disabled={!canEdit}
              style={{ marginLeft: 8 }}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label><b>Label Background Colour:</b></label>
            <input
              type="color"
              value={edit.labelBgColour}
              onChange={e => handleChange('labelBgColour', padHexColour(e.target.value))}
              disabled={!canEdit}
              style={{ marginLeft: 8, verticalAlign: 'middle' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label><b>Description:</b></label>
            <textarea
              rows={4}
              value={edit.description}
              onChange={e => handleChange('description', e.target.value)}
              disabled={!canEdit}
              style={{ width: 'calc(100% - 60px)', fontSize: 15, fontFamily: 'inherit', marginLeft: 8 }}
            />
          </div>

          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <label><b>Type Icon:</b></label>
            {edit.iconUrl ? (
              <img
                src={edit.iconUrl}
                alt="Type Icon"
                style={{ width: 32, height: 32, borderRadius: 4, border: '1px solid #444', marginLeft: 8 }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span style={{ color: '#888', marginLeft: 8, fontSize: 14 }}>None</span>
            )}
            {canEdit && (
              <button
                onClick={() => setMode('icon-upload')}
                style={{ marginLeft: 12 }}
              >
                Change Icon
              </button>
            )}
          </div>

          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <label><b>Image:</b></label>
            {edit.imageUrl ? (
              <img
                src={edit.imageUrl}
                alt="POI"
                style={{ maxWidth: 160, maxHeight: 90, borderRadius: 5, border: '1px solid #555', marginLeft: 8 }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span style={{ color: '#888', fontSize: 14, marginLeft: 8 }}>No image</span>
            )}
            {canEdit && (
              <button
                onClick={() => setMode('image-upload')}
                style={{ marginLeft: 12 }}
              >
                Change Image
              </button>
            )}
          </div>

          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center', gap: 12 }}>
            {canEdit && (
              <button
                onClick={handleDelete}
                style={{
                  background: '#f44c2b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 22px',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                Delete
              </button>
            )}

            <button
              onClick={onClose}
              style={{
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 22px',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              style={{
                background: dirty ? '#4cc153' : '#666',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 22px',
                fontWeight: 700,
                fontSize: 15,
                cursor: dirty ? 'pointer' : 'not-allowed',
                flexShrink: 0,
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default OfflineEditPOI;
